import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  ReportExportRequest,
  SlowMoverRow,
  TopCustomerRow,
  TopItemRow,
  TopMoversReport,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { ReportPeriodButton, ReportSearchField, ReportToolbar } from '@/components/ReportToolbar';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { ExportMenu } from '@/components/ExportMenu';
import { downloadReportExport } from '@/features/reports/api';
import { exportSubtitle } from '@/features/reports/export-table';
import type { ExportFormat } from '@/features/reports/api';
import { fetchTopMovers, topMoversKeys } from '@/features/reports/top-movers-api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, ''));
const sum = <T,>(rows: readonly T[], pick: (row: T) => number) => rows.reduce((acc, row) => acc + pick(row), 0);
const LIMITS = [10, 20, 50, 100] as const;

type TabKey = 'quantity' | 'revenue' | 'customers' | 'slow';
const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'quantity', label: 'Top by quantity' },
  { key: 'revenue', label: 'Top by revenue' },
  { key: 'customers', label: 'Top customers' },
  { key: 'slow', label: 'Slow movers' },
];

const rank = (index: number) => <span className="tabular-nums text-slate-400">{index + 1}</span>;

function itemColumns(rows: readonly TopItemRow[]): Column<TopItemRow>[] {
  return [
    { key: 'rank', header: '#', cell: (row) => rank(rows.indexOf(row)) },
    { key: 'productName', header: 'Product', cell: (row) => row.productName },
    { key: 'quantitySold', header: 'Qty sold', align: 'right', cell: (row) => qty(row.quantitySold) },
    { key: 'quantityReturned', header: 'Qty returned', align: 'right', visibility: 'hidden sm:table-cell', cell: (row) => qty(row.quantityReturned) },
    { key: 'netQuantity', header: 'Net qty', align: 'right', cell: (row) => qty(row.netQuantity) },
    { key: 'revenue', header: 'Revenue', align: 'right', visibility: 'hidden sm:table-cell', cell: (row) => money(row.revenue) },
    { key: 'returnedValue', header: 'Returned', align: 'right', visibility: 'hidden sm:table-cell', cell: (row) => money(row.returnedValue) },
    { key: 'netRevenue', header: 'Net revenue', align: 'right', cell: (row) => money(row.netRevenue) },
  ];
}

function customerColumns(rows: readonly TopCustomerRow[]): Column<TopCustomerRow>[] {
  return [
    { key: 'rank', header: '#', cell: (row) => rank(rows.indexOf(row)) },
    { key: 'customerName', header: 'Customer', cell: (row) => row.customerName },
    { key: 'phone', header: 'Phone', visibility: 'hidden sm:table-cell', cell: (row) => row.phone || '—' },
    { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
    { key: 'total', header: 'Spend', align: 'right', visibility: 'hidden sm:table-cell', cell: (row) => money(row.total) },
    { key: 'returns', header: 'Returns', align: 'right', visibility: 'hidden sm:table-cell', cell: (row) => money(row.returns) },
    { key: 'net', header: 'Net spend', align: 'right', cell: (row) => money(row.net) },
    { key: 'lastVisit', header: 'Last visit', cell: (row) => row.lastVisit },
  ];
}

const SLOW_COLUMNS: Column<SlowMoverRow>[] = [
  { key: 'productName', header: 'Product', cell: (row) => row.productName },
  {
    key: 'quantitySold',
    header: 'Sold in range',
    align: 'right',
    cell: (row) =>
      row.quantitySold <= 0 ? (
        <Badge tone="warning" dot>
          None
        </Badge>
      ) : (
        qty(row.quantitySold)
      ),
  },
  { key: 'lastSaleDate', header: 'Last sold', cell: (row) => row.lastSaleDate?.slice(0, 10) ?? 'Never' },
  {
    key: 'daysSinceLastSale',
    header: 'Days idle',
    align: 'right',
    cell: (row) => (row.daysSinceLastSale === null ? '—' : String(row.daysSinceLastSale)),
  },
  { key: 'stockOnHand', header: 'On hand', align: 'right', cell: (row) => qty(row.stockOnHand) },
  { key: 'stockValue', header: 'Stock value', align: 'right', cell: (row) => money(row.stockValue) },
];

/** Stock value is priced at cost — hidden, on screen and in the export, from a login without cost access. */
function slowColumns(canViewCost: boolean): Column<SlowMoverRow>[] {
  return canViewCost ? SLOW_COLUMNS : SLOW_COLUMNS.filter((column) => column.key !== 'stockValue');
}

function matches(search: string, ...values: string[]): boolean {
  const term = search.trim().toLowerCase();
  return term === '' || values.some((value) => value.toLowerCase().includes(term));
}

function hasRows(tab: TabKey, report: TopMoversReport): boolean {
  return (
    tab === 'quantity'
      ? report.byQuantity
      : tab === 'revenue'
        ? report.byRevenue
        : tab === 'customers'
          ? report.topCustomers
          : report.slowMovers
  ).length > 0;
}

function itemSource(tab: 'quantity' | 'revenue', report: TopMoversReport): TopItemRow[] {
  return tab === 'quantity' ? report.byQuantity : report.byRevenue;
}

function filterItems(source: readonly TopItemRow[], search: string): TopItemRow[] {
  return source.filter((row) => matches(search, row.productName));
}

function filterCustomers(source: readonly TopCustomerRow[], search: string): TopCustomerRow[] {
  return source.filter((row) => matches(search, row.customerName, row.phone));
}

function filterSlow(source: readonly SlowMoverRow[], search: string, maxSold: string): SlowMoverRow[] {
  const ceiling = maxSold.trim() === '' ? null : Number(maxSold);
  return source.filter(
    (row) =>
      matches(search, row.productName) &&
      (ceiling === null || Number.isNaN(ceiling) || row.quantitySold <= ceiling),
  );
}

interface PanelProps {
  tab: TabKey;
  report: TopMoversReport;
  search: string;
  maxSold: string;
  canViewCost: boolean;
}

/**
 * The visible tab's table, rows filtered exactly as on screen, for the
 * server's PDF / Excel renderer. Rank stays the row's place in the full
 * list, as the # column shows it.
 */
function buildExport(
  { tab, report, search, maxSold, canViewCost }: PanelProps,
  filters: ReportFilters,
  branches: readonly BranchDto[],
): ReportExportRequest {
  const label = TABS.find((item) => item.key === tab)?.label ?? '';
  const base = {
    title: `Top & slow movers — ${label}`,
    subtitle: exportSubtitle(report, { branchId: filters.branchId, branches }),
    from: report.from,
    to: report.to,
    notes: search.trim() !== '' ? [`Search: “${search.trim()}”`] : [],
  };
  if (tab === 'quantity' || tab === 'revenue') {
    const source = itemSource(tab, report);
    return {
      ...base,
      columns: [
        { header: '#', align: 'right', numeric: true },
        { header: 'Product', width: 2 },
        { header: 'Qty sold', align: 'right', numeric: true },
        { header: 'Qty returned', align: 'right', numeric: true },
        { header: 'Net qty', align: 'right', numeric: true },
        { header: 'Revenue', align: 'right', money: true },
        { header: 'Returned', align: 'right', money: true },
        { header: 'Net revenue', align: 'right', money: true },
      ],
      rows: filterItems(source, search).map((row) => [
        String(source.indexOf(row) + 1),
        row.productName,
        qty(row.quantitySold),
        qty(row.quantityReturned),
        qty(row.netQuantity),
        money(row.revenue),
        money(row.returnedValue),
        money(row.netRevenue),
      ]),
    };
  }
  if (tab === 'customers') {
    const source = report.topCustomers;
    return {
      ...base,
      columns: [
        { header: '#', align: 'right', numeric: true },
        { header: 'Customer', width: 2 },
        { header: 'Phone' },
        { header: 'Bills', align: 'right', numeric: true },
        { header: 'Spend', align: 'right', money: true },
        { header: 'Returns', align: 'right', money: true },
        { header: 'Net spend', align: 'right', money: true },
        { header: 'Last visit' },
      ],
      rows: filterCustomers(source, search).map((row) => [
        String(source.indexOf(row) + 1),
        row.customerName,
        row.phone,
        String(row.bills),
        money(row.total),
        money(row.returns),
        money(row.net),
        row.lastVisit,
      ]),
    };
  }
  const rows = filterSlow(report.slowMovers, search, maxSold);
  const ceiling = maxSold.trim();
  return {
    ...base,
    notes: [...base.notes, ...(ceiling !== '' ? [`Sold at most ${ceiling} in range.`] : [])],
    columns: [
      { header: 'Product', width: 2 },
      { header: 'Sold in range', align: 'right', numeric: true },
      { header: 'Last sold' },
      { header: 'Days idle', align: 'right', numeric: true },
      { header: 'On hand', align: 'right', numeric: true },
      ...(canViewCost ? [{ header: 'Stock value', align: 'right' as const, money: true }] : []),
    ],
    rows: rows.map((row) => [
      row.productName,
      qty(row.quantitySold),
      row.lastSaleDate?.slice(0, 10) ?? 'Never',
      row.daysSinceLastSale === null ? '' : String(row.daysSinceLastSale),
      qty(row.stockOnHand),
      ...(canViewCost ? [money(row.stockValue)] : []),
    ]),
    totals: {
      label: `${rows.length} products`,
      // The label fills the first column (Product); cells cover the rest.
      cells: [
        '',
        '',
        '',
        qty(sum(rows, (r) => r.stockOnHand)),
        ...(canViewCost ? [money(sum(rows, (r) => r.stockValue))] : []),
      ],
    },
  };
}

function Panel({ tab, report, search, maxSold, canViewCost }: PanelProps) {
  if (tab === 'quantity' || tab === 'revenue') {
    const source = itemSource(tab, report);
    const rows = filterItems(source, search);
    return (
      <DataTable
        caption={tab === 'quantity' ? 'Top items by net quantity sold' : 'Top items by net revenue'}
        columns={itemColumns(source)}
        rows={rows}
        rowKey={(row) => row.productName}
      />
    );
  }
  if (tab === 'customers') {
    const rows = filterCustomers(report.topCustomers, search);
    return (
      <DataTable
        caption="Top customers by net spend"
        columns={customerColumns(report.topCustomers)}
        rows={rows}
        rowKey={(row) => row.customerName}
      />
    );
  }
  const rows = filterSlow(report.slowMovers, search, maxSold);
  return (
    <DataTable
      caption="Slow movers: stocked products, least sold first"
      columns={slowColumns(canViewCost)}
      rows={rows}
      rowKey={(row) => row.productName}
      totals={{
        label: `${rows.length} products`,
        cells: {
          stockOnHand: qty(sum(rows, (r) => r.stockOnHand)),
          ...(canViewCost ? { stockValue: money(sum(rows, (r) => r.stockValue)) } : {}),
        },
      }}
    />
  );
}

/**
 * Best sellers, best customers and dead stock for one window. Sales follow
 * the till: Void bills and opening balances are not sales, and returns net
 * on their own date. Slow movers are every product with stock on hand now,
 * least sold in the window first.
 */
export function TopMoversPage() {
  const { profile } = usePortal();
  const canViewCost = profile?.canViewCost !== false;
  const [filters, setFilters] = useReportFilters();
  const [tab, setTab] = useState<TabKey>('quantity');
  const [limit, setLimit] = useState<number>(20);
  const [search, setSearch] = useState('');
  const [maxSold, setMaxSold] = useState('');

  const query = useQuery({
    queryKey: topMoversKeys.report(filters, limit),
    queryFn: ({ signal }) => fetchTopMovers(filters, limit, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <PageHeader
        title="Top & slow movers"
        description="Best-selling items and customers in the window, and stocked products that barely sold."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
        <div role="tablist" aria-label="Top and slow movers" className="-mb-px flex flex-wrap gap-1">
          {TABS.map((item) => {
            const selected = item.key === tab;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`movers-tab-${item.key}`}
                aria-selected={selected}
                aria-controls={`movers-panel-${item.key}`}
                className={`border-b-2 px-3 py-2 text-sm font-medium ${
                  selected
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <div className="pb-2">
          <ReportPeriodButton
            from={filters.from}
            to={filters.to}
            onChange={(range) => setFilters({ from: range.from, to: range.to })}
          />
        </div>
      </div>
      <ReportToolbar>
        <ReportSearchField
          value={search}
          onChange={setSearch}
          placeholder={tab === 'customers' ? 'Search customer or phone' : 'Search product'}
        />
        {tab === 'slow' ? (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Sold at most
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              className="input h-9 w-24 py-1"
              value={maxSold}
              placeholder="any"
              onChange={(event) => setMaxSold(event.target.value)}
            />
          </label>
        ) : (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Show top
            <select
              className="input h-9 py-1 pr-9"
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            >
              {LIMITS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {query.data && hasRows(tab, query.data) ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) =>
                downloadReportExport(
                  buildExport(
                    { tab, report: query.data, search, maxSold, canViewCost },
                    filters,
                    profile?.branches ?? [],
                  ),
                  format,
                )
              }
            />
          </div>
        ) : null}
      </ReportToolbar>
      <div role="tabpanel" id={`movers-panel-${tab}`} aria-labelledby={`movers-tab-${tab}`}>
        <AsyncSection
          state={query}
          isEmpty={(report) => !hasRows(tab, report)}
          emptyTitle={
            tab === 'slow'
              ? 'No products with stock on hand'
              : tab === 'customers'
                ? 'No named customers billed in this window'
                : 'No sales in this window'
          }
          skeleton={<TableSkeleton columns={8} />}
        >
          {(report) => (
            <Panel tab={tab} report={report} search={search} maxSold={maxSold} canViewCost={canViewCost} />
          )}
        </AsyncSection>
      </div>
    </div>
  );
}
